import gsap from "gsap";
import ScrollTrigger from "gsap/ScrollTrigger";

gsap.registerPlugin(ScrollTrigger);

/**
 * FadeIn — lightweight scroll-triggered reveal helper.
 *
 * Markup:
 *   <div data-fade-in>…</div>              single element fades up on scroll
 *   <ul data-fade-in-children>…</ul>       direct children stagger in on scroll
 *
 * Per-element tuning via data attributes (all optional):
 *   data-fade-delay="0.2"      delay before the animation starts (seconds)
 *   data-fade-duration="1"     animation duration (seconds)
 *   data-fade-y="40"           vertical travel (px, use a negative value to drop down)
 *   data-fade-x="0"            horizontal travel (px)
 *   data-fade-stagger="0.12"   gap between children (data-fade-in-children only, seconds)
 *   data-fade-start="top 85%"  ScrollTrigger start position
 */

const DEFAULTS = {
  duration: 0.8,
  delay: 0.2,
  y: 20,
  x: 0,
  stagger: 0.12,
  start: "top bottom",
  ease: "power1.out",
};

const prefersReducedMotion = window.matchMedia(
  "(prefers-reduced-motion: reduce)",
).matches;

function num(value, fallback) {
  const parsed = parseFloat(value);
  return Number.isNaN(parsed) ? fallback : parsed;
}

function options(el) {
  return {
    duration: num(el.dataset.fadeDuration, DEFAULTS.duration),
    delay: num(el.dataset.fadeDelay, DEFAULTS.delay),
    y: num(el.dataset.fadeY, DEFAULTS.y),
    x: num(el.dataset.fadeX, DEFAULTS.x),
    stagger: num(el.dataset.fadeStagger, DEFAULTS.stagger),
    start: el.dataset.fadeStart || DEFAULTS.start,
  };
}

function reveal(el, targets, opts) {
  gsap.set(targets, { willChange: "transform, opacity" });

  gsap.from(targets, {
    autoAlpha: 0,
    y: opts.y,
    x: opts.x,
    duration: opts.duration,
    delay: opts.delay,
    stagger: opts.stagger,
    ease: DEFAULTS.ease,
    scrollTrigger: {
      trigger: el,
      start: opts.start,
      once: true,
    },
    onComplete: () => gsap.set(targets, { willChange: "auto" }),
  });
}

export default function FadeIn(root = document) {
  const singles = root.querySelectorAll("[data-fade-in]");
  const groups = root.querySelectorAll("[data-fade-in-children]");

  // Reduced motion: skip the animation, just make sure everything is visible.
  if (prefersReducedMotion) {
    gsap.set(
      [...singles, ...groups, ...[...groups].flatMap((g) => [...g.children])],
      { autoAlpha: 1, clearProps: "transform" },
    );
    return;
  }

  singles.forEach((el) => reveal(el, el, options(el)));

  groups.forEach((el) => {
    const children = el.children;
    if (!children.length) return;
    reveal(el, children, options(el));
  });
}
